import { formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { FireDashboardStats } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, PageHeader, RoleNotice, StatCard, StatusBadge, Spinner } from '../../ui/components';

const OFFICER_ROLES = ['FIRE_INSPECTOR', 'CHIEF_FIRE_OFFICER'];

/** Fire & Rescue — chief officer dashboard: aggregate operational stats (M06). */
export function FireDashboard() {
  const { persona } = useAuth();
  const isOfficer = persona ? persona.roles.some((r) => OFFICER_ROLES.includes(r)) : false;
  const statsQ = useApiQuery<FireDashboardStats>('/api/v1/m06/dashboard');
  const s = statsQ.data;

  return (
    <div>
      <PageHeader title="Fire & Rescue Dashboard" subtitle="Aggregate operational stats across certificates, inspections, hydrants, and incidents (M06)." />

      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {statsQ.error ? <ErrorNote message={statsQ.error} /> : null}
      {statsQ.loading ? <Spinner /> : null}

      {s ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard label="Overdue rectifications" value={s.overdueRectifications} tone={s.overdueRectifications > 0 ? 'bad' : 'good'} />
            <StatCard label="Inspections booked today" value={s.inspectionsBookedToday} />
            <StatCard label="Overdue inspections" value={s.overdueInspections} tone={s.overdueInspections > 0 ? 'warn' : 'good'} />
            <StatCard label="Open complaints" value={s.openComplaints} tone={s.openComplaints > 0 ? 'warn' : 'good'} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="card p-4">
              <div className="mb-3 text-sm font-bold text-slate-900">Fire certificate applications by state</div>
              <div className="space-y-2">
                {Object.entries(s.applicationsByState).length === 0 ? (
                  <p className="text-sm text-slate-400">None on file.</p>
                ) : (
                  Object.entries(s.applicationsByState).map(([state, count]) => (
                    <div key={state} className="flex items-center justify-between text-sm">
                      <StatusBadge status={state} />
                      <span className="tabular-nums font-semibold">{count}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="card p-4">
              <div className="mb-3 text-sm font-bold text-slate-900">Hydrants by status</div>
              <div className="space-y-2">
                {Object.entries(s.hydrantsByStatus).length === 0 ? (
                  <p className="text-sm text-slate-400">No hydrants registered yet.</p>
                ) : (
                  Object.entries(s.hydrantsByStatus).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between text-sm">
                      <StatusBadge status={status} />
                      <span className="tabular-nums font-semibold">{count}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="card p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-bold text-slate-900">Open incidents by severity</div>
                <span className="text-sm font-semibold tabular-nums text-slate-700">{s.openIncidents} open</span>
              </div>
              <div className="space-y-2">
                {Object.entries(s.openIncidentsBySeverity).length === 0 ? (
                  <p className="text-sm text-slate-400">No open incidents.</p>
                ) : (
                  Object.entries(s.openIncidentsBySeverity).map(([severity, count]) => (
                    <div key={severity} className="flex items-center justify-between text-sm">
                      <StatusBadge status={severity} />
                      <span className="tabular-nums font-semibold">{count}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-400">Generated {formatDateTime(s.generatedAt)}</p>
        </div>
      ) : null}
    </div>
  );
}
